import React, { useContext, useState } from 'react'
import {
  Button,
  ButtonGroup,
  FlexGrid,
  Search as SearchBar,
  Spacer,
  Tags,
  Typography,
  useResponsiveProp,
} from '@telus-uds/components-web'
import { CaretDown } from '@telus-uds/palette-allium/build/web/icons'
import { SiteBuilderContext } from 'src/siteBuilder/renderer/context/SiteBuilderContext'
import { CARD_FILTER_I18N } from './CardFilter.i18n'
import { matchSearchTerms, matchTags, renderCard } from './CardFilter.helpers'

export interface TagProps {
  id: string
  label: string
  urlTag?: string
}

export type FilterCard = {
  id: string
  tags?: TagProps[]
  content: React.ReactElement
  background?: 'alternative' | 'subtle' | 'grid'
  searchTerms?: string
  hasGutter?: boolean
}

export type CardFilterProps = {
  cards?: FilterCard[]
  filterTags?: TagProps[]
  useSingleTag?: boolean
  enableSearch?: boolean
  resultsText?: string
  showLoadMoreBtn?: boolean
  buttonText?: string
  heading?: string
  equalCardHeight?: boolean
}

const SHOW_ALL_ID = 'all'
const SHOW_BY = 9

const CardFilter = ({
  cards,
  filterTags,
  useSingleTag = true,
  enableSearch = false,
  resultsText,
  showLoadMoreBtn = true,
  buttonText,
  heading,
  equalCardHeight = true,
}: CardFilterProps) => {
  const { locale } = useContext(SiteBuilderContext)
  const { allLabel, resultsLabel } = CARD_FILTER_I18N[locale.language]

  const isMobile = useResponsiveProp({ xs: true, md: false })
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [searchInputValue, setSearchInputValue] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [cardsToShow, setCardsToShow] = useState(showLoadMoreBtn === false ? cards?.length ?? 0 : SHOW_BY)

  // Derive unique filter tags from cards if not explicitly provided
  const resolvedFilterTags: TagProps[] =
    filterTags ?? Array.from(new Map(cards?.flatMap((card) => card.tags ?? []).map((tag) => [tag.id, tag])).values())

  const tagFilteredCards =
    selectedTags.length > 0 ? cards?.filter((card) => card.tags?.some((tag) => selectedTags.includes(tag.id))) : cards

  // A card matches the search if its tag labels OR its additional searchTerms contain the query
  const filteredCards = searchTerm
    ? tagFilteredCards?.filter((card) => {
        const lowerSearch = searchTerm.toLowerCase()
        return matchTags(card, lowerSearch) || matchSearchTerms(card, lowerSearch)
      })
    : tagFilteredCards

  const visibleCards = filteredCards?.slice(0, cardsToShow)
  const showMoreEnabled = showLoadMoreBtn && (filteredCards?.length ?? 0) > cardsToShow

  const content = visibleCards?.map((card) => renderCard(card, equalCardHeight))
  const allItem = { id: SHOW_ALL_ID, label: allLabel }
  const buttonGroupValues = selectedTags.length === 0 ? [SHOW_ALL_ID] : selectedTags
  const tagsValues = selectedTags.length === 0 ? [SHOW_ALL_ID] : selectedTags

  const onSingleTagChange = (selected: string[]) => {
    if (selected.length === 0) return
    setCardsToShow(SHOW_BY)
    if (selected.includes(SHOW_ALL_ID)) {
      setSelectedTags([])
    } else {
      setSelectedTags(selected)
    }
  }

  const onMultiTagChange = (selected: string[]) => {
    if (selected.length === 0) {
      setSelectedTags([])
      setCardsToShow(SHOW_BY)
      return
    }

    const allJustSelected = selectedTags.length > 0 && selected.includes(SHOW_ALL_ID)
    if (allJustSelected) {
      setSelectedTags([])
      setCardsToShow(SHOW_BY)
      return
    }
    setSelectedTags(selected.filter((id) => id !== SHOW_ALL_ID))
    setCardsToShow(SHOW_BY)
  }

  const onSubmitSearch = (value: string) => {
    setSearchTerm(value)
    setCardsToShow(SHOW_BY)
  }

  const onClearSearch = () => {
    setSearchInputValue('')
    setSearchTerm('')
    setCardsToShow(SHOW_BY)
  }

  const onShowMore = () => {
    setCardsToShow((prev) => prev + SHOW_BY)
  }

  return (
    <FlexGrid>
      {!!heading && (
        <>
          <FlexGrid.Row>
            <FlexGrid.Col>
              <Typography block variant={{ size: 'h1' }}>
                {heading}
              </Typography>
            </FlexGrid.Col>
          </FlexGrid.Row>
          <Spacer space={6} />
        </>
      )}
      {(resolvedFilterTags.length > 0 || enableSearch) && (
        <FlexGrid.Row {...(enableSearch && isMobile && { xsReverse: true, mdReverse: false })}>
          {resolvedFilterTags.length > 0 && (
            <FlexGrid.Col {...(enableSearch && { md: 8, xl: 9 })}>
              {useSingleTag ? (
                <ButtonGroup
                  items={[allItem, ...resolvedFilterTags]}
                  values={buttonGroupValues}
                  onChange={onSingleTagChange}
                />
              ) : (
                <Tags items={[allItem, ...resolvedFilterTags]} values={tagsValues} onChange={onMultiTagChange} />
              )}
              <Spacer space={4} />
            </FlexGrid.Col>
          )}
          {enableSearch && (
            <FlexGrid.Col md={4} xl={3}>
              <SearchBar
                value={searchInputValue}
                onChange={setSearchInputValue}
                onClear={onClearSearch}
                onSubmit={onSubmitSearch}
                copy={locale.language}
              />
              <Spacer space={{ xs: 3, md: 0 }} />
            </FlexGrid.Col>
          )}
        </FlexGrid.Row>
      )}
      {!!resultsText && (
        <>
          <FlexGrid.Row>
            <FlexGrid.Col>
              <Typography block variant={{ colour: 'secondary' }}>
                {resultsLabel(filteredCards?.length ?? 0, cards?.length ?? 0, resultsText)}
              </Typography>
            </FlexGrid.Col>
          </FlexGrid.Row>
          <Spacer space={5} />
        </>
      )}
      {!!content && <FlexGrid.Row>{content}</FlexGrid.Row>}
      {showMoreEnabled && (
        <>
          <Spacer space={3} />
          <FlexGrid.Row>
            <FlexGrid.Col horizontalAlign="center">
              <Button
                icon={CaretDown}
                iconPosition="right"
                tokens={{ textLine: 'none' }}
                variant={{ text: true }}
                onPress={onShowMore}
              >
                {buttonText}
              </Button>
            </FlexGrid.Col>
          </FlexGrid.Row>
        </>
      )}
    </FlexGrid>
  )
}

export default CardFilter
